import React, { useState, useEffect } from "react";
import { Scrollbars } from "react-custom-scrollbars-2";
import { Card, CardBody } from "@windmill/react-ui";
import UseCitySubmit from "hooks/UseCitySubmit";
import Title from "components/form/Title";
import LabelArea from "components/form/LabelArea";
import CityServices from "services/CityServices";
import InputArea from "components/form/InputArea";
import InputName from "components/form/InputName";
import SelectCountry from "components/form/SelectCountry";
import Error from "components/form/Error";
import DrawerButton from "components/form/DrawerButton";
import SelectStateSingle from "components/form/SelectStateSingle";
import { Button } from "@windmill/react-ui";
import { FiX } from "react-icons/fi";
import UploaderSlider from "components/image-uploader/UploaderSlider";
import TagInputTwo from "components/common/TagInputTwo";
import { notifyError } from "utils/toast";

const CityDrawer = ({ id }) => {

  const [imageFields, setImageFields] = useState([]);
  const [nameError, setNameError] = useState(false);
  const {
    register,
    trigger,
    handleSubmit,
    onSubmit,
    errors,
    handleCountryChange,
    setCityUrl1,
    variants,
    selectedState,
    loading,
    addVariant,
    removeVariant,
    selectedCountryId,
    handleStateChange,
    getCityData,
    clearErrors,
    setError,
  } = UseCitySubmit(id, setImageFields);

  useEffect(() => {
    if (setCityUrl1) {
      setImageFields([
        { label: "Image 1", name: "", url: "", setUrl: setCityUrl1 },
      ]);
    }
  }, [setCityUrl1]);

  useEffect(() => {
    if (id) {
      const fetchCityData = async () => {
        const cityData = await CityServices.getCityById(id);
        if (cityData && cityData.image) {
          const populatedFields = Object.keys(cityData.image).map(
            (key, index) => ({
              label: `Image ${index + 1}`,
              name: key,
              url: cityData.image[key],
              setUrl: (newUrl) => {
                const newFields = [...imageFields];
                newFields[index].url = newUrl;
                setImageFields(newFields);
              },
            })
          );

          setImageFields(populatedFields);
        }
      };
      fetchCityData();
    }
    // eslint-disable-next-line
  }, [id, getCityData]);

  const addImageField = () => {
    if (imageFields.length < 6) {
      setImageFields((prevFields) => [
        ...prevFields,
        {
          label: `Image ${prevFields.length + 1}`,
          name: "",
          url: "",
          setUrl: setCityUrl1,
        },
      ]);
    }
  };

  const removeImageField = (index) => {
    setImageFields((prevFields) => prevFields.filter((_, i) => i !== index));
  };

const handleFormSubmit = (data) => {
  const uploadedImages = imageFields.filter((field) => field.url).length;

  if (nameError) {
    notifyError("City name already exists");
    return;
  }

  // if (uploadedImages < 3) {
  //   notifyError("Please upload at least 3 images!");
  //   return;
  // }

  const names = [];
  for (const field of imageFields) {
    if (field.url && !field.name.trim()) {
      notifyError("Image name is required for all uploaded images.");
      return;
    }
    if (field.name.trim()) {
      if (names.includes(field.name.trim())) {
        notifyError("Image names must be unique.");
        return;
      }
      names.push(field.name.trim());
    }
  }

  const formData = { ...data };

  formData.country_name = selectedCountryId;
  formData.id_state = selectedState;

  // ✅ Sight seeing can be NULL
  formData.sight_seeing =
    variants && variants.length > 0 ? variants : null;

  imageFields.forEach((field, index) => {
    formData[`image${index + 1}`] = field.url;
    formData[`image${index + 1}_name`] = field.name;
  });

  onSubmit(formData);
};


  const checkCityExists = async (city) => {
    try {
      const response = await CityServices.checkCity({ city: city });

      if (response.status && response.data.city_exists) {
        setError("name", {
          message: "City already exists!",
        });
        setNameError(true);
      } else {
        clearErrors("name");
        setNameError(false);
      }
    } catch (error) {
      console.error("Error checking city:", error);
    }
  };
  return (
    <>
      <div className="w-full relative p-6 border-b border-gray-100 bg-gray-50 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300">
        <Title register={register} title={id ? "Edit City" : "Add City"} />
      </div>
      <Scrollbars className="w-full md:w-7/12 lg:w-8/12 xl:w-8/12 relative dark:bg-gray-700 dark:text-gray-200">
        <Card className="flex-grow scrollbar-hide w-full max-h-full">
          <CardBody>
            <form onSubmit={handleSubmit(handleFormSubmit)}>
              <div className="px-2 flex-grow scrollbar-hide w-full max-h-full pb-20 ">
                {/* State and City Name Inputs */}
                <div className="grid grid-cols-12 gap-5 mb-6 items-center">
                  <div className="col-span-6">
                    <LabelArea label={"Country"} required />
                    <SelectCountry
                      register={register}
                      label="Country"
                      name="country"
                      selectedCountryId={selectedCountryId}
                      handleCountryChange={handleCountryChange}
                      readOnly={id?true:false}
                    />
                    <Error errorName={errors.country} />
                  </div>

                  <div className="col-span-6">
                    <LabelArea label="Select State" required />
                    <SelectStateSingle
                      register={register}
                      label="State"
                      name="state"
                      selectedCountryId={selectedCountryId}
                      selectedState={selectedState}
                      handleStateChange={handleStateChange}
                      // readOnly={id?true:false}
                    />
                    <Error errorName={errors.state} />
                  </div>

                  <div className="col-span-6">
                    <LabelArea label="City Name" required />
                    <InputName
                      trigger={trigger}
                      register={register}
                      label="City"
                      name="name"
                      type="text"
                      placeholder="City"
                      onBlur={(e) => checkCityExists(e.target.value)}
                    />
                    <Error errorName={errors.name} />
                  </div>

                  {/* Sight Seeing Input */}
                  <div className="col-span-6">
                    <LabelArea label="Sight Seeing"  />
                    <TagInputTwo
                      notes={variants}
                      addNote={addVariant}
                      removeNote={removeVariant}
                      placeholder="Press enter to add variant"
                    />
                    <Error errorName={errors.sight_seeing} />
                  </div>
                </div>
                {/* Dynamic Image Upload Inputs */}
                {imageFields.map(({ label, name, url, setUrl }, index) => (
                  <div className="grid grid-cols-12 gap-5 mb-6" key={index}>
                    <div className="col-span-6">
                      <LabelArea label={`${label} name`} required={index < 3} />
                      <InputArea
                        required={false}
                        trigger={trigger}
                        register={register}
                        label={`${label} name`}
                        name={`image${index + 1}_name`}
                        type="text"
                        value={name}
                        placeholder="Image name"
                        onChange={(e) => {
                          const newFields = [...imageFields];
                          newFields[index].name = e.target.value;
                          setImageFields(newFields);
                        }}
                      />
                      {url ? (
                        <Error errorName={errors[`image${index + 1}_name`]} />
                      ) : null}
                    </div>

                    <div className="col-span-6">
                      <LabelArea label={label} required={index < 3} />
                      {url ? (
                        <div className="flex">
                          <img
                            style={{
                              objectFit: "cover",
                              borderRadius: "10px",
                              width: "300px",
                              height: "150px",
                            }}
                            src={`${process.env.REACT_APP_API_IMAGE_URL}${url}`}
                            alt={`${label}`}
                          />
                          <FiX
                            size={20}
                            className="cursor-pointer"
                            onClick={() => removeImageField(index)}
                          />
                        </div>
                      ) : (
                        <UploaderSlider
                          is_multiple={true}
                          folder="city"
                          name_merge="City"
                          imageUrl={url}
                          setImageUrl={(newUrl) => {
                            const newFields = [...imageFields];
                            newFields[index].url = newUrl;
                            setImageFields(newFields);
                          }}
                          no_of_image={1}
                        />
                      )}
                    </div>
                  </div>
                ))}

                {/* Add Image Field Button */}
                <div className="flex justify-end">
                  <Button
                    type="button"
                    className="bg-green-500 text-white"
                    onClick={addImageField}
                    disabled={imageFields.length >= 6}
                  >
                    Add Image
                  </Button>
                </div>

                {/* Notify Error if Submit Disabled */}
              </div>
              <DrawerButton
                isSubmitting={loading}
                id={id}
                title={id ? "Edit" : "Save"}
              />
            </form>
          </CardBody>
        </Card>
      </Scrollbars>
    </>
  );
};

export default CityDrawer;
